import {
  createClipPathData,
  createRectGrid,
  type MaskRect,
  type Rect,
} from "./maskGeometry";

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * Puts a DOM element (the VS Code workbench) at the rect that gaze reports for `rectId`, and cuts
 * a hole in it for each gaze overlay, so that the overlay shows above the element.
 *
 * `rectId` must be the `rect_id` that gaze gives to `report_rect` and `report_overlays`. The
 * caller puts its elements in `content`.
 */
export function mountMask(
  container: HTMLElement,
  rectId: string,
): { root: HTMLDivElement; content: HTMLDivElement } {
  // The elements use `url(#<id>)` to find the clip path, so the id must be unique on the page.
  const clipPathId = `${rectId}-main-clip-path`;

  const root = document.createElement("div");
  Object.assign(root.style, {
    position: "fixed",
    inset: "0",
    zIndex: "100000",
    clipPath: `url(#${clipPathId})`,
  });

  const content = document.createElement("div");
  Object.assign(content.style, { position: "absolute", inset: "0" });

  // The backdrop darkens the element while gaze shows a modal.
  const backdrop = document.createElement("div");
  Object.assign(backdrop.style, {
    position: "absolute",
    inset: "0",
    backgroundColor: "rgba(12, 12, 12, 0.6)",
    zIndex: "100001",
    clipPath: `url(#${clipPathId})`,
  });

  root.append(content, backdrop);
  container.append(root);

  const svg = document.createElementNS(SVG_NS, "svg");
  Object.assign(svg.style, {
    position: "fixed",
    inset: "0",
    zIndex: "100001",
    pointerEvents: "none",
  });
  svg.setAttribute("width", "100%");
  svg.setAttribute("height", "100%");
  const defs = document.createElementNS(SVG_NS, "defs");
  const clipPath = document.createElementNS(SVG_NS, "clipPath");
  clipPath.id = clipPathId;
  clipPath.setAttribute("clipPathUnits", "userSpaceOnUse");
  defs.append(clipPath);
  svg.append(defs);
  document.body.append(svg);

  // Gaze reports these values in a window event. The mask changes the DOM in the same event,
  // so that the element moves in the same frame as gaze.
  let lastRect: Rect | null = null;
  let lastOverlayRects: MaskRect[] = [];
  let lastModalState = false;
  let lastEntityPickMode = false;
  let lastWorkbenchSize: { width: number; height: number } | null = null;

  const updateLayout = () => {
    const rect = lastRect;
    if (rect) {
      root.style.left = `${rect.x}px`;
      root.style.top = `${rect.y}px`;
      root.style.width = `${rect.width}px`;
      root.style.height = `${rect.height}px`;
      root.style.borderRadius = rect.borderRadius
        ? `${rect.borderRadius}px`
        : "0";
      root.style.display =
        rect.width <= 0 || rect.height <= 0 ? "none" : "block";

      const workbench = root.querySelector<HTMLElement>(".monaco-workbench");
      const last = lastWorkbenchSize;
      const sizeChanged =
        !last || last.width !== rect.width || last.height !== rect.height;
      if (workbench && sizeChanged) {
        lastWorkbenchSize = { width: rect.width, height: rect.height };
        workbench.style.width = `${rect.width}px`;
        workbench.style.height = `${rect.height}px`;

        // VS Code runs a full layout on each resize event, so send one only when the size changes.
        window.dispatchEvent(new Event("resize"));
      }
    }

    // Expand overlay rects by 1px to show egui's outside stroke
    const expandedOverlayRects = lastOverlayRects.map((r) => ({
      ...r,
      x: r.x - 1,
      y: r.y - 1,
      width: r.width + 2,
      height: r.height + 2,
    }));

    // Combine overlapping rectangles into non-overlapping ones
    const combinedRects = rect ? createRectGrid(expandedOverlayRects) : [];

    const mainPath = rect ? createClipPathData(combinedRects, rect) : "";
    clipPath.innerHTML = `<path d="${mainPath}" fillRule="evenodd" />`;
  };

  const updateBackdrop = () => {
    const active =
      lastModalState || (rectId === "vscode_editor" && lastEntityPickMode);
    backdrop.style.display = active ? "block" : "none";
    root.style.pointerEvents = active ? "none" : "auto";
    backdrop.style.pointerEvents = active ? "none" : "auto";
  };
  updateBackdrop();

  window.addEventListener("gaze:report-rect", (event) => {
    const { rect_id, x, y, width, height, borderRadius } = (
      event as CustomEvent
    ).detail;
    if (rect_id === rectId) {
      lastRect = { x, y, width, height, borderRadius };
      updateLayout();
    }
  });

  window.addEventListener("gaze:report-overlays", (event) => {
    const { rect_id, rects_json } = (event as CustomEvent).detail;
    if (rect_id === rectId) {
      lastOverlayRects = JSON.parse(rects_json);
      updateLayout();
    }
  });

  window.addEventListener("gaze:report-modal-state", (event) => {
    lastModalState = (event as CustomEvent).detail.has_modal;
    updateBackdrop();
  });

  window.addEventListener("gaze:report-entity-pick-mode", (event) => {
    lastEntityPickMode = (event as CustomEvent).detail.active;
    updateBackdrop();
  });

  return { root, content };
}
